import { useState } from "react";
import { motion } from "framer-motion";
import { ABOUT, OWNER_PHOTO } from "@/lib/content";
import { SectionHead, Reveal } from "@/components/Kinetic";

const Monogram = () => (
  <div data-testid="about-photo-fallback" className="relative flex h-full w-full items-center justify-center bg-emerald-deep" role="img" aria-label="FAIZ monogram">
    <div aria-hidden="true" className="absolute inset-6 rounded-[24px] border border-white/10" />
    <div aria-hidden="true" className="absolute -bottom-24 -right-24 h-72 w-72 rounded-full border-[24px] border-white/[0.06]" />
    <svg viewBox="0 0 48 48" className="relative w-[46%]" aria-hidden="true">
      <path d="M17 36V12.5h14M17 23.5h11" fill="none" stroke="#FFFFFF" strokeWidth="4.2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="32" cy="34" r="2.6" fill="#8FE3C2" />
    </svg>
    <span className="absolute bottom-8 left-8 font-mono text-[11px] uppercase tracking-[0.2em] text-white/60">FAIZ</span>
  </div>
);

const Portrait = () => {
  const [failed, setFailed] = useState(false);
  const show = OWNER_PHOTO && !failed;
  return (
    <div className="relative pb-4 pr-4 sm:pb-5 sm:pr-5">
      <div aria-hidden="true" className="absolute bottom-0 left-8 right-0 top-8 rounded-[32px] bg-emerald-deep" />
      <div className="relative aspect-[4/5] w-full overflow-hidden rounded-[28px] bg-ink-2 shadow-[0_30px_60px_-30px_rgba(23,33,29,0.55)] ring-1 ring-black/5">
        {show ? (
          <picture>
            <source type="image/webp" srcSet={`${OWNER_PHOTO.webp640} 640w, ${OWNER_PHOTO.webp1080} 1080w`} sizes="(min-width: 1024px) 40vw, (min-width: 640px) 420px, 90vw" />
            <img
              src={OWNER_PHOTO.jpg}
              width={OWNER_PHOTO.width}
              height={OWNER_PHOTO.height}
              alt={OWNER_PHOTO.alt}
              loading="lazy"
              decoding="async"
              onError={() => setFailed(true)}
              data-testid="about-photo"
              className="h-full w-full object-cover"
            />
          </picture>
        ) : (
          <Monogram />
        )}
      </div>
    </div>
  );
};

export default function About() {
  return (
    <section id="about" data-testid="about-section" className="relative overflow-hidden bg-white py-24 lg:py-36">
      <div className="wrap grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-20">
        <Reveal className="mx-auto w-full max-w-[420px] lg:col-span-5 lg:max-w-none" y={40}>
          <motion.div whileHover={{ scale: 1.01 }} transition={{ duration: 0.5 }}>
            <Portrait />
          </motion.div>
        </Reveal>
        <div className="lg:col-span-7">
          <SectionHead eyebrow="ABOUT" title={ABOUT.heading} hl={[3]} testId="about-heading" />
          <div className="mt-10 max-w-2xl">
            {ABOUT.body.map((p, i) => (
              <Reveal key={i} delay={0.1 * i}>
                <p data-testid={`about-body-${i + 1}`} className={`leading-relaxed ${i === 0 ? "text-lg text-ink sm:text-xl" : "mt-6 text-base text-muted sm:text-lg"}`}>
                  {p}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
